import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaHandHoldingUsd,
  FaClock,
  FaCashRegister,
  FaHandshake,
  FaExclamationTriangle,
  FaUser,
  FaFileInvoice,
  FaUserTie,
  FaMoneyBill,
  FaUserSlash,
} from "react-icons/fa";
import { MdOutlineRule } from "react-icons/md";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import "./DemoDashboard.css"

// Quick Access
const quickAccess = [
  { id: "customer", label: "Customer", icon: <FaUser size={22} />, link: "/CustomerReport" },
  { id: "employee", label: "Employee", icon: <FaUserTie size={22} />, link: "/CustomerReport" },
  { id: "loan", label: "Loan", icon: <FaMoneyBill size={22} />, link: "/Report" },
  { id: "collection", label: "Collection", icon: <FaCashRegister size={22} />, link: "/CollectionReport" },
  { id: "due-report", label: "Due Report", icon: <FaFileInvoice size={22} />, link: "/EmiDueReport" },
  // { id: "close", label: "Close Account", icon: <FaUserSlash size={22} />, link: "/AccountClosureRequest" },
  { id: "loan-scheme", label: "Loan Scheme", icon: <MdOutlineRule size={22} />, link: "/LoanScheme" },
];

// KPI Data
const kpis = [
  { id: "collection", title: "Collection", icon: <FaHandHoldingUsd />, value: 0, deltaPct: 0, subtitle: "This Month" },
  { id: "pending-collection", title: "Pending Collection", icon: <FaClock />, value: 0, deltaPct: 0, subtitle: "This Month" },
  { id: "disb", title: "Disbursement", icon: <FaHandshake />, value: 0, deltaPct: 0, subtitle: "This Month" },
  { id: "pending-disb", title: "Pending Disbursement", icon: <FaExclamationTriangle />, value: 0, deltaPct: 0, subtitle: "This Month" },
];

// Balance Data
const balances = [
  { label: "Total Branches", value: 0 },
  { label: "Total", value: 0 },
  { label: "Cashbook", value: -0 },
  { label: "Bankbook", value: 0 },
  { label: "Charges", value: -0 },
  { label: "Collection", value: 0 },
  { label: "Disbursement", value: 0 },
  { label: "Outstanding", value: 0 },
];

// Transactions
const recentTx = [
  { id: "TXN-98231", type: "Collection", party: "Rahul Kumar", amount: 0, branch: "Branch 1", time: "10:22 AM" },
  { id: "TXN-98218", type: "Disbursement", party: "Meera Shah", amount: 0, branch: "Branch 3", time: "09:05 AM" },
  { id: "TXN-98207", type: "Charge", party: "Processing", amount: -0, branch: "HQ", time: "Yesterday" },
  { id: "TXN-98199", type: "Collection", party: "Amit Verma", amount: 0, branch: "Branch 2", time: "Yesterday" },
];

// Branch Performance
const branchPerformance = [
  { branch: "Branch 1", collection: 0, disbursement: 0, pending: 0, npaPct: 0},
  { branch: "Branch 2", collection: 0, disbursement: 0, pending: 0, npaPct: 0 },
  { branch: "Branch 3", collection: 0, disbursement: 0, pending: 0, npaPct: 0 },
  { branch: "Branch 4", collection: 0, disbursement: 0, pending: 0, npaPct: 0 },
];

// Chart Data
const chartData = [
  { day: "Mon", collection: 0, disbursement: 0 },
  { day: "Tue", collection: 0, disbursement: 0 },
  { day: "Wed", collection: 0, disbursement: 0 },
  { day: "Thu", collection: 0, disbursement: 0 },
  { day: "Fri", collection: 0, disbursement: 0 },
  { day: "Sat", collection: 0, disbursement: 0 },
  { day: "Sun", collection: 0, disbursement: 0 },
];

function rupee(n) {
  const sign = n < 0 ? "-" : "";
  const v = Math.abs(n);
  return `${sign}₹${v.toLocaleString("en-IN")}`;
}

function Chip({ tone = "neutral", children }) {
  return <span className={`Admin-dashboard-design-front-page-chip Admin-dashboard-design-front-page-chip-${tone}`}>{children}</span>;
}

function Card({ title, subtitle, right, children, className }) {
  return (
    <section className={`Admin-dashboard-design-front-page-card ${className || ""}`}>
      {(title || right) && (
        <header className="Admin-dashboard-design-front-page-card-header">
          <div>
            <div className="Admin-dashboard-design-front-page-card-subtitle">{subtitle}</div>
            <h3 className="Admin-dashboard-design-front-page-card-title">{title}</h3>
          </div>
          {right}
        </header>
      )}
      <div>{children}</div>
    </section>
  );
}

export default function DemoDashboard() {
  const navigate = useNavigate();

  const [filters, setFilters] = useState({ branch: "All Branches", period: "Last 30 days" });
  const branches = ["All Branches", "Branch 1", "Branch 2", "Branch 3", "Branch 4"];
  const periods = ["Today", "This Week", "Last 30 days", "This Quarter", "This Year"];

  const totals = useMemo(() => {
    const totalCollection = branchPerformance.reduce((a, b) => a + b.collection, 0);
    const totalDisbursement = branchPerformance.reduce((a, b) => a + b.disbursement, 0);
    const totalPending = branchPerformance.reduce((a, b) => a + b.pending, 0);
    return { totalCollection, totalDisbursement, totalPending };
  }, []);

  return (
    <div className="Admin-dashboard-design-front-page-container">
      {/* Top Bar */}
      <div className="Admin-dashboard-design-front-page-topbar">
        <div>
          <div className="Admin-dashboard-design-front-page-topbar-subtitle">Admin Console</div>
          <h1 className="Admin-dashboard-design-front-page-topbar-title">Banking Dashboard</h1>
        </div>

        <div className="Admin-dashboard-design-front-page-search">
          <input placeholder="Search customers, loans, transactions..." />
        </div>

        <div className="Admin-dashboard-design-front-page-filters">
          <select value={filters.branch} onChange={(e) => setFilters((f) => ({ ...f, branch: e.target.value }))}>
            {branches.map((b) => <option key={b}>{b}</option>)}
          </select>
          <select value={filters.period} onChange={(e) => setFilters((f) => ({ ...f, period: e.target.value }))}>
            {periods.map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
      </div>

      {/* KPI Row */}
      <div className="Admin-dashboard-design-front-page-kpi-row">
        {kpis.map((k) => {
          const up = k.deltaPct >= 0;
          return (
            <Card
              key={k.id}
              title={k.title}
              subtitle={k.subtitle}
              right={
                <Chip tone={up ? "up" : "down"}>
                  <span style={{ transform: `rotate(${up ? 0 : 180}deg)`, fontWeight: 900 }}>▲</span>
                  {Math.abs(k.deltaPct)}%
                </Chip>
              }
            >
              <div className="Admin-dashboard-design-front-page-kpi-content">
                <div className="Admin-dashboard-design-front-page-kpi-icon">{k.icon}</div>
                <div>
                  <div className="Admin-dashboard-design-front-page-kpi-value">{rupee(k.value)}</div>
                  <div className="Admin-dashboard-design-front-page-kpi-label">vs previous period</div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Main Grid */}
      <div className="Admin-dashboard-design-front-page-main-grid">
        {/* Collections & Disbursements */}
        <Card
          title="Collections & Disbursements"
          subtitle={`${filters.branch} • ${filters.period}`}
          right={
            <div className="Admin-dashboard-design-front-page-summary-chips">
              <Chip tone="info">Collection: {rupee(totals.totalCollection)}</Chip>
              <Chip tone="neutral">Disb: {rupee(totals.totalDisbursement)}</Chip>
              <Chip tone="warn">Pending: {rupee(totals.totalPending)}</Chip>
            </div>
          }
        >
          {/* Charts */}
          <div className="Admin-dashboard-design-front-page-charts">
            <div className="Admin-dashboard-design-front-page-chart">
              <h4>Collections Trend</h4>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <defs>
                    <linearGradient id="colGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4CAF50" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#4CAF50" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333"/>
                  <XAxis dataKey="day" stroke="#aaa"/>
                  <YAxis stroke="#aaa"/>
                  <Tooltip contentStyle={{ background: "#1e1e1e", border: "none" }} />
                  <Legend />
                  <Line type="monotone" dataKey="collection" stroke="#4CAF50" strokeWidth={2} dot={false} fill="url(#colGradient)" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="Admin-dashboard-design-front-page-chart">
              <h4>Disbursement Trend</h4>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={chartData}>
                  <defs>
                    <linearGradient id="disbGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2196F3" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#2196F3" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333"/>
                  <XAxis dataKey="day" stroke="#aaa"/>
                  <YAxis stroke="#aaa"/>
                  <Tooltip contentStyle={{ background: "#1e1e1e", border: "none" }} />
                  <Legend />
                  <Line type="monotone" dataKey="disbursement" stroke="#2196F3" strokeWidth={2} dot={false} fill="url(#disbGradient)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Branch Table */}
          <div className="Admin-dashboard-design-front-page-branch-table">
            <div className="Admin-dashboard-design-front-page-table-label">Branch Performance</div>
            <div className="Admin-dashboard-design-front-page-table-wrapper">
              <table>
                <thead>
                  <tr>
                    {["Branch", "Collection", "Disbursement", "Pending", "NPA %"].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {branchPerformance.map((row, idx) => (
                    <tr key={row.branch} className={idx % 2 ? "even" : "odd"}>
                      <td>{row.branch}</td>
                      <td>{rupee(row.collection)}</td>
                      <td>{rupee(row.disbursement)}</td>
                      <td><Chip tone={row.pending > 20000 ? "warn" : "neutral"}>{rupee(row.pending)}</Chip></td>
                      <td><Chip tone={row.npaPct > 1.5 ? "down" : "up"}>{row.npaPct}%</Chip></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>

        {/* Right Column */}
        <div className="Admin-dashboard-design-front-page-right-col">
          {/* Quick Actions */}
          <Card title="Quick Actions" subtitle="Navigate fast">
  <div className="Admin-dashboard-ram-quick-actions">
    {quickAccess.map((qa) => (
      <button
        key={qa.id}
        onClick={() => navigate(qa.link)}
        className="Admin-dashboard-ram-quick-btn"
      >
        <div className="Admin-dashboard-ram-quick-icon">{qa.icon}</div>
        <div className="Admin-dashboard-ram-quick-label">{qa.label}</div>
      </button>
    ))}
  </div>
</Card>


          {/* Balance Snapshot */}
          <Card title="Balance Snapshot" subtitle="Key ledgers">
            <div className="Admin-dashboard-design-front-page-balance-grid">
              {balances.map((b) => (
                <div key={b.label} className="Admin-dashboard-design-front-page-balance-item">
                  <div className="Admin-dashboard-design-front-page-balance-label">{b.label}</div>
                  <div className={`Admin-dashboard-design-front-page-balance-value ${b.value < 0 ? "negative" : ""}`}>
                    {rupee(b.value)}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Lower Grid */}
      <div className="Admin-dashboard-design-front-page-lower-grid">
        {/* Transactions */}
        <Card title="Recent Transactions" subtitle="Latest activity">
          <div className="Admin-dashboard-design-front-page-table-wrapper">
            <table>
              <thead>
                <tr>
                  {["Txn ID", "Type", "Party", "Amount", "Branch", "Time"].map((h) => <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {recentTx.map((t, idx) => (
                  <tr key={t.id} className={idx % 2 ? "even" : "odd"}>
                    <td>{t.id}</td>
                    <td><Chip tone={t.type === "Collection" ? "up" : t.type === "Disbursement" ? "neutral" : "down"}>{t.type}</Chip></td>
                    <td>{t.party}</td>
                    <td>{rupee(t.amount)}</td>
                    <td>{t.branch}</td>
                    <td>{t.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="Admin-dashboard-design-front-page-view-all">
            {/* <button onClick={() => navigate("/BankingReports")}>View All</button> */}
          </div>
        </Card>

        {/* Tasks & Alerts */}
        <Card title="Tasks & Alerts" subtitle="Operational">
          <ul className="Admin-dashboard-design-front-page-tasks">
            <li className="warn">
              <div>
                <div className="title">pending disbursements</div>
                <div className="desc">Review and approve before EOD.</div>
              </div>
              <button onClick={() => navigate("/PendingDisbursal")}>Review</button>
            </li>

            <li className="success">
              <div>
                <div className="title">KYC updates required</div>
                <div className="desc">Customers with expired documents.</div>
              </div>
              <button onClick={() => navigate("/CustomerKyc")}>Update KYC</button>
            </li>

            <li className="info">
              <div>
                <div className="title">System health: All services operational</div>
                <div className="desc">No incidents reported in the last 24h.</div>
              </div>
              {/* <button onClick={() => navigate("/CompanyBankAccount")}>View Logs</button> */}
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
}